import React from 'react';
import Card from '@/components/ui/Card';
import { TeamHealthMetrics } from '@/types';

interface TeamHealthWidgetProps {
  metrics: TeamHealthMetrics;
  totalPlayers?: number;
  injuredPlayers?: number;
}

const TeamHealthWidget: React.FC<TeamHealthWidgetProps> = ({ metrics, totalPlayers = 0, injuredPlayers = 0 }) => {
  const getHealthScore = () => {
    // Calculate overall health score as percentage of healthy (non-injured) players
    if (totalPlayers === 0) return 0;
    
    const healthyPlayers = totalPlayers - injuredPlayers;
    const healthPercentage = (healthyPlayers / totalPlayers) * 100;
    
    return Math.max(0, Math.min(100, Math.round(healthPercentage)));
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBarColor = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatPercentage = (value: number) => `${Math.round(value)}%`;

  const healthScore = getHealthScore();

  return (
    <Card className="h-full">
      <div className="p-4 h-full flex flex-col">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Team Health Overview</h3>
        
        {/* Overall Health Score */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Overall Health Score</span>
            <span className={`text-2xl font-bold ${getScoreColor(healthScore)}`}>{healthScore}/100</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-300 ${getProgressBarColor(healthScore)}`}
              style={{ width: `${healthScore}%` }}
            ></div>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Avg Fitness</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {Math.round(metrics.fitnessAverage || 0)}/100
                </p>
              </div>
              <div className="text-blue-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Injury Rate</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatPercentage(metrics.injuryRate || 0)}
                </p>
              </div>
              <div className="text-red-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Training Attendance</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatPercentage(metrics.trainingAttendance || 0)}
                </p>
              </div>
              <div className="text-green-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Recovering</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {metrics.recoveryPlayers + metrics.questionablePlayers}
                </p>
              </div>
              <div className="text-yellow-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between text-sm">
            <div className="text-center">
              <p className="font-medium text-orange-600">{metrics.recoveryPlayers}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Recovering</p>
            </div>
            <div className="text-center">
              <p className="font-medium text-yellow-600">{metrics.questionablePlayers}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Questionable</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TeamHealthWidget;